"use client";

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ChevronRight, Cpu, Layers, Sparkles, ArrowRight, CircleCheck as CheckCircle2, Workflow, Zap, GitCommitHorizontal as GitCommit } from 'lucide-react';
interface NodeItem {
  code: string;
  step: string;
  tier: string;
  title: {
    vi: string;
    en: string;
  };
  to?: string;
  isFinal?: boolean;
}
interface TierItem {
  id: string;
  number: string;
  courses: string;
  title: {
    vi: string;
    en: string;
  };
  items: Array<{
    vi: string;
    en: string;
  }>;
}
const pathwayNodes: NodeItem[] = [{
  code: "D1",
  step: "01",
  tier: "T1",
  title: {
    vi: "Bé làm kỹ sư",
    en: "Little Engineer"
  },
  to: "/RoboticsCourseD1Page"
}, {
  code: "D2",
  step: "02",
  tier: "T1",
  title: {
    vi: "Thành phố của em",
    en: "My Smart City"
  },
  to: "/RoboticsCourseD2Page"
}, {
  code: "D3",
  step: "03",
  tier: "T2",
  title: {
    vi: "Cỗ máy kỳ diệu",
    en: "Miraculous Machines"
  },
  to: "/RoboticsCourseD3Page"
}, {
  code: "D4",
  step: "04",
  tier: "T2",
  title: {
    vi: "Động cơ nhí",
    en: "Junior Motor Mechanics"
  },
  to: "/RoboticsCourseD4Page"
}, {
  code: "HP1",
  step: "05",
  tier: "T3",
  title: {
    vi: "Kỹ sư cơ khí nhí",
    en: "Junior Mechanical Engineer"
  },
  to: "/RoboticsCourseHP1Page"
}, {
  code: "HP2",
  step: "06",
  tier: "T3",
  title: {
    vi: "Robot biết cảm nhận",
    en: "Sensing Robots"
  },
  to: "/RoboticsCourseHP2Page"
}, {
  code: "HP3",
  step: "07",
  tier: "T3",
  title: {
    vi: "Xe robot đầu tiên",
    en: "First Robot Car"
  },
  to: "/RoboticsCourseHP3Page"
}, {
  code: "HP4",
  step: "08",
  tier: "T4",
  title: {
    vi: "Xe robot thông minh",
    en: "Smart Robot Car"
  },
  to: "/RoboticsCourseHP4Page"
}, {
  code: "HP5",
  step: "09",
  tier: "T4",
  title: {
    vi: "Thành phố xe tự lái",
    en: "Autonomous Driving City"
  },
  to: "/RoboticsCourseHp5Page"
}, {
  code: "HP6",
  step: "10",
  tier: "T5",
  title: {
    vi: "Robot nhiệm vụ",
    en: "Mission Challenge Robot"
  },
  to: "/RoboticsCourseHP6Page"
}, {
  code: "AI",
  step: "11",
  tier: "T5",
  title: {
    vi: "ENJOY AI PREPARATION",
    en: "ENJOY AI Preparation"
  },
  isFinal: true
}];
const progressionTiers: TierItem[] = [{
  id: "tier-1",
  number: "01",
  courses: "D1 – D2",
  title: {
    vi: "ROBOTICS SƠ KHỞI",
    en: "Early Robotics"
  },
  items: [{
    vi: "Chế tạo",
    en: "Build"
  }, {
    vi: "Tư duy cơ khí",
    en: "Mechanical thinking"
  }, {
    vi: "Logic lập trình trực quan",
    en: "Visual programming logic"
  }]
}, {
  id: "tier-2",
  number: "02",
  courses: "D3 – D4",
  title: {
    vi: "ROBOTICS NỀN TẢNG",
    en: "Foundation Robotics"
  },
  items: [{
    vi: "Cảm biến",
    en: "Sensors"
  }, {
    vi: "Điều kiện",
    en: "Conditions"
  }, {
    vi: "Vòng lặp",
    en: "Loops"
  }, {
    vi: "Biến số",
    en: "Variables"
  }]
}, {
  id: "tier-3",
  number: "03",
  courses: "HP1 – HP3",
  title: {
    vi: "ROBOTICS DI ĐỘNG",
    en: "Mobile Robotics"
  },
  items: [{
    vi: "Chuyển động",
    en: "Movement"
  }, {
    vi: "Khoảng cách",
    en: "Distance"
  }, {
    vi: "Góc quay",
    en: "Turns"
  }, {
    vi: "Hoạch định lộ trình",
    en: "Route planning"
  }]
}, {
  id: "tier-4",
  number: "04",
  courses: "HP4 – HP5",
  title: {
    vi: "ROBOTICS TỰ HÀNH",
    en: "Autonomous Robotics"
  },
  items: [{
    vi: "Cảm biến",
    en: "Sensors"
  }, {
    vi: "Dò line",
    en: "Line following"
  }, {
    vi: "Xử lý chướng ngại vật",
    en: "Obstacle handling"
  }, {
    vi: "Ra quyết định",
    en: "Decision making"
  }]
}, {
  id: "tier-5",
  number: "05",
  courses: "HP6 – ENJOY AI",
  title: {
    vi: "ROBOTICS NHIỆM VỤ",
    en: "Mission Robotics"
  },
  items: [{
    vi: "Cơ cấu",
    en: "Mechanisms"
  }, {
    vi: "Chức năng nhiệm vụ",
    en: "Mission functions"
  }, {
    vi: "Chiến thuật",
    en: "Strategy"
  }, {
    vi: "Độ tin cậy",
    en: "Reliability"
  }]
}];
export default function RoboticsPathwayConnection() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const handleLangChange = () => {
      const saved = localStorage.getItem("roboto_lang");
      if (saved === "en" || saved === "vi") {
        setLang(saved);
      }
    };
    handleLangChange();
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="roboticspathwayconnection" data-nav="dark" className="dark bg-[#0a0f1d] text-slate-100 relative overflow-hidden py-16 md:py-24 border-b border-slate-800">
      {/* High-tech deep blue blueprint grid background */}
      <div className="absolute inset-0 pointer-events-none opacity-25 bg-[linear-gradient(to_right,rgba(56,189,248,0.15)_1px,transparent_1px),linear-gradient(to_bottom,rgba(56,189,248,0.15)_1px,transparent_1px)] bg-[size:32px_32px]" aria-hidden="true" />

      {/* Deep-blue & cyan glow accents */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="absolute bottom-10 -right-32 w-96 h-96 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
          <Badge variant="outline" className="mb-4 font-mono text-xs uppercase tracking-widest text-cyan-400 border-cyan-500/40 bg-cyan-950/40 px-3 py-1">
            <Cpu className="w-3.5 h-3.5 mr-1.5 text-cyan-400" />
            {lang === "vi" ? "LỘ TRÌNH ĐÀO TẠO CHUẨN QUỐC TẾ" : "INTERNATIONAL STANDARDS PATHWAY"}
          </Badge>

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white uppercase font-serif">
            {lang === "vi" ? "TỪ NHỮNG BƯỚC ĐẦU TIÊN ĐẾN NHIỆM VỤ ROBOT" : "From First Steps to Robot Missions"}
          </h2>
          <div className="mt-3 h-1 w-20 bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400 mx-auto rounded-full" />
        </div>

        {/* BLOCK A: 11-Course Visual Diagram Timeline */}
        <div className="mb-20">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6 px-1">
            <h3 className="text-xs font-mono uppercase tracking-widest text-cyan-300 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-cyan-400 inline-block animate-pulse" />
              <Workflow className="w-4 h-4 text-cyan-400" />
              {lang === "vi" ? "Mạng lưới khóa học liên tiếp" : "Sequential Course Network"}
            </h3>
            <span className="text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-1 rounded-full border border-slate-800">
              {lang === "vi" ? "11 Cột mốc phát triển" : "11 Development Milestones"}
            </span>
          </div>

          {/* Visual Sequence Grid/Flow Diagram */}
          <div className="p-4 sm:p-6 md:p-8 rounded-2xl bg-slate-900/90 border border-slate-800/80 shadow-2xl relative overflow-hidden backdrop-blur-xl">
            {/* Ambient circuit background pattern */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(14,165,233,0.08),transparent_50%)] pointer-events-none" />

            {/* Desktop Sequential Bus Line */}
            <div className="hidden lg:block absolute top-[50%] left-8 right-8 h-0.5 bg-gradient-to-r from-blue-500/40 via-cyan-400/50 to-amber-500/50 -translate-y-1/2 pointer-events-none z-0" />

            <motion.div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 relative z-10" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: "-50px"
          }}>
              {pathwayNodes.map((node, index) => {
              const isLast = index === pathwayNodes.length - 1;
              if (node.isFinal) {
                return <motion.div key="final-node" data-index={index} variants={itemVariants} className="col-span-1 sm:col-span-2 md:col-span-1 flex items-center">
                      <div className="w-full h-full flex flex-col justify-between p-3.5 rounded-xl bg-gradient-to-br from-amber-500/20 via-orange-500/10 to-slate-900 border border-amber-500/60 text-amber-200 shadow-lg shadow-amber-500/10 group">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40">
                            STEP {node.step}
                          </span>
                          <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
                        </div>
                        <div className="mt-1">
                          <span className="text-[10px] font-mono text-amber-400 block tracking-wider uppercase mb-0.5">
                            TIER 05 • MASTERY
                          </span>
                          <span className="font-semibold text-xs sm:text-sm tracking-wide text-white block">
                            {node.title[lang]}
                          </span>
                        </div>
                      </div>
                    </motion.div>;
              }
              return <motion.div key={node.code} data-index={index} variants={itemVariants} className="relative flex items-center">
                    <Link to={node.to || "#"} className="w-full group relative flex flex-col justify-between p-3.5 rounded-xl bg-slate-900/90 border border-slate-800 hover:border-cyan-400/80 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/10 hover:-translate-y-0.5">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-cyan-950/80 text-cyan-300 border border-cyan-500/40 group-hover:bg-cyan-400 group-hover:text-slate-950 transition-colors">
                          {node.code}
                        </span>
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-[10px] text-slate-400 bg-slate-800/80 px-1.5 py-0.5 rounded">
                            {node.tier}
                          </span>
                          {!isLast && <ChevronRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-cyan-400 transition-colors" />}
                        </div>
                      </div>

                      <div>
                        <span className="text-[10px] font-mono text-slate-500 block mb-0.5">
                          STEP {node.step}
                        </span>
                        <span className="text-xs sm:text-sm font-medium text-slate-200 group-hover:text-cyan-300 transition-colors block line-clamp-1">
                          {node.title[lang]}
                        </span>
                      </div>
                    </Link>
                  </motion.div>;
            })}
            </motion.div>
          </div>
        </div>

        {/* BLOCK B: 5-Tier Technical Progression Diagram Matrix */}
        <div className="mb-14">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-8 border-b border-slate-800 pb-4">
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2 uppercase tracking-wide font-serif">
                <Layers className="w-5 h-5 text-cyan-400" />
                {lang === "vi" ? "Khung Tiến Trình Năng Lực Kỹ Thuật" : "Technical Competency Progression"}
              </h3>
            </div>
            <p className="text-xs text-slate-400 font-mono">
              {lang === "vi" ? "Dựa trên chương trình D1–D4 & HP1–HP6" : "Based on D1–D4 & HP1–HP6 Curriculum"}
            </p>
          </div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-50px"
        }}>
            {progressionTiers.map((tier, tierIdx) => <motion.div key={tier.id} data-index={tierIdx} variants={itemVariants}>
                <Card className="h-full bg-slate-900/90 border-slate-800 hover:border-cyan-500/50 transition-colors duration-300 relative overflow-hidden flex flex-col justify-between shadow-xl">
                  {/* Top glowing blueprint line */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400" />

                  <CardContent className="p-5 flex flex-col h-full justify-between gap-4">
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-mono text-xs font-bold text-cyan-400 tracking-widest">
                          TIER {tier.number}
                        </span>
                        <Badge variant="outline" className="text-[10px] font-mono border-slate-700 text-slate-300 bg-slate-800/50">
                          {tier.courses}
                        </Badge>
                      </div>

                      <h4 className="text-sm font-bold text-white mb-4 uppercase tracking-wide border-b border-slate-800 pb-2 font-serif">
                        {tier.title[lang]}
                      </h4>

                      <ul className="space-y-2.5">
                        {tier.items.map((item, itemIdx) => <li key={`${tier.id}-item-${itemIdx}`} data-index={itemIdx} className="text-xs text-slate-300 flex items-start gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-cyan-400 shrink-0 mt-0.5" />
                            <span>{item[lang]}</span>
                          </li>)}
                      </ul>
                    </div>

                    <div className="pt-3 border-t border-slate-800/80 text-[10px] font-mono text-slate-400 flex items-center justify-between">
                      <span>{lang === "vi" ? `Cấp độ ${tier.number}` : `Level ${tier.number}`}</span>
                      <span className="text-cyan-400/80">{tier.items.length} {lang === "vi" ? "kỹ năng" : "skills"}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>

          {/* Caption for Block B */}
          <p className="text-center text-xs text-slate-400 mt-6 font-mono italic">
            * {lang === "vi" ? "Sơ đồ phản ánh toàn bộ nội dung huấn luyện kiến thức & kỹ năng cốt lõi từ các khóa học D1–D4 và HP1–HP6." : "This framework reflects core knowledge & skill milestones mapped directly across courses D1–D4 and HP1–HP6."}
          </p>
        </div>

        {/* Closing Call To Action Button */}
        <div className="text-center pt-4">
          <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 rounded-xl shadow-lg shadow-primary/20 text-sm sm:text-base tracking-wide">
            <Link to="/RoboticsLearningPathwayPage">
              {lang === "vi" ? "XEM LỘ TRÌNH ROBOTICS" : "View the Robotics Pathway"}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}